"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ShoppingCart, DollarSign, TrendingUp, Clock } from "lucide-react";

interface KpiCardsProps {
  data: {
    ordersCreated: number;
    revenue: number;
    conversionRate: number;
    avgOrderToQuote: number | null;
    avgQuoteToPaid: number | null;
    avgPickupToDelivered: number | null;
  };
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatHours(hours: number | null): string {
  if (hours === null) return "-";
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  return `${days}d`;
}

export function KpiCards({ data }: KpiCardsProps) {
  const cards = [
    {
      title: "Orders Created",
      value: data.ordersCreated.toString(),
      icon: ShoppingCart,
      description: "Total orders in period",
    },
    {
      title: "Revenue",
      value: formatCurrency(data.revenue),
      icon: DollarSign,
      description: "From invoiced orders",
    },
    {
      title: "Conversion Rate",
      value: `${data.conversionRate}%`,
      icon: TrendingUp,
      description: "Orders with invoice",
    },
    {
      title: "Avg Order to Quote",
      value: formatHours(data.avgOrderToQuote),
      icon: Clock,
      description: "Time to send quote",
    },
    {
      title: "Avg Quote to Paid",
      value: formatHours(data.avgQuoteToPaid),
      icon: Clock,
      description: "Time to payment",
    },
    {
      title: "Avg Pickup to Delivered",
      value: formatHours(data.avgPickupToDelivered),
      icon: Clock,
      description: "Fulfillment time",
    },
  ];

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {cards.map((card) => (
        <Card key={card.title}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {card.title}
            </CardTitle>
            <card.icon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{card.value}</div>
            <p className="text-xs text-muted-foreground">{card.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
